// Pure helpers for diffing two Lighthouse rawReport blobs.
// Same LHR type subset as CategoryAccordion — duplicated locally to keep diff.ts standalone.

export type AuditResult = 'pass' | 'average' | 'fail' | 'info' | 'na'

interface LhrAudit {
  id: string
  title: string
  description?: string
  score: number | null
  displayValue?: string
  scoreDisplayMode:
    | 'numeric' | 'binary' | 'informative' | 'notApplicable' | 'manual' | 'error'
}

interface LhrAuditRef { id: string }

interface LhrCategory {
  id: string
  title: string
  score: number | null
  auditRefs: LhrAuditRef[]
}

interface LhrRaw {
  categories?: Record<string, LhrCategory>
  audits?: Record<string, LhrAudit>
}

export function getAuditResult(audit: LhrAudit): AuditResult {
  if (audit.scoreDisplayMode === 'notApplicable') return 'na'
  if (audit.scoreDisplayMode === 'informative' || audit.scoreDisplayMode === 'manual') return 'info'
  if (audit.score === null) return 'info'
  if (audit.score >= 0.9) return 'pass'
  if (audit.score >= 0.5) return 'average'
  return 'fail'
}

export const CATEGORY_ORDER = ['performance', 'accessibility', 'best-practices', 'seo'] as const

export const CATEGORY_LABELS: Record<string, string> = {
  performance:      'Performance',
  accessibility:    'Accessibility',
  'best-practices': 'Best Practices',
  seo:              'SEO',
}

export interface ChangedAudit {
  id: string
  title: string
  before: AuditResult
  after: AuditResult
  beforeDisplay?: string
  afterDisplay?: string
  /** -1 = regression, +1 = improvement, 0 = lateral (e.g. info ↔ na) */
  direction: -1 | 0 | 1
}

const RANK: Record<AuditResult, number> = { fail: 0, average: 1, info: 2, na: 3, pass: 4 }

function compareDirection(before: AuditResult, after: AuditResult): -1 | 0 | 1 {
  const b = RANK[before]
  const a = RANK[after]
  if (a > b) return 1
  if (a < b) return -1
  return 0
}

export interface CategoryDiff {
  key: string
  label: string
  scoreBefore: number | null
  scoreAfter: number | null
  changed: ChangedAudit[]
}

/**
 * Returns one CategoryDiff per known category (only if the category exists in
 * at least one report). `changed` contains only audits whose result-state
 * differs between the two reports, sorted by regressions first.
 */
export function diffReports(
  rawBefore: Record<string, unknown> | null | undefined,
  rawAfter: Record<string, unknown> | null | undefined,
): CategoryDiff[] {
  const a = (rawBefore ?? {}) as unknown as LhrRaw
  const b = (rawAfter ?? {}) as unknown as LhrRaw

  return CATEGORY_ORDER
    .filter(key => a.categories?.[key] || b.categories?.[key])
    .map(key => {
      const catA = a.categories?.[key]
      const catB = b.categories?.[key]

      const refs = catA?.auditRefs ?? catB?.auditRefs ?? []
      const seen = new Set<string>()
      const changed: ChangedAudit[] = []

      for (const ref of refs) {
        if (seen.has(ref.id)) continue
        seen.add(ref.id)

        const auditA = a.audits?.[ref.id]
        const auditB = b.audits?.[ref.id]
        if (!auditA && !auditB) continue

        const resultA: AuditResult = auditA ? getAuditResult(auditA) : 'na'
        const resultB: AuditResult = auditB ? getAuditResult(auditB) : 'na'

        if (resultA === resultB) continue

        changed.push({
          id: ref.id,
          title: auditB?.title ?? auditA?.title ?? ref.id,
          before: resultA,
          after: resultB,
          beforeDisplay: auditA?.displayValue,
          afterDisplay: auditB?.displayValue,
          direction: compareDirection(resultA, resultB),
        })
      }

      // Regressions first, then lateral, then improvements
      changed.sort((x, y) => x.direction - y.direction)

      return {
        key,
        label: CATEGORY_LABELS[key] ?? key,
        scoreBefore: catA?.score != null ? Math.round(catA.score * 100) : null,
        scoreAfter:  catB?.score != null ? Math.round(catB.score * 100) : null,
        changed,
      }
    })
}
